import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Grid,
  Divider,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";

import apiClient, { resolveFileUrl, useApiQuery } from "../api/client";

const emptyPlan = { name: "", points: "", badge: "", order: 0, isActive: true };

const AdminPaymentPlans = () => {
  const { data: plansData, isLoading, refetch } = useApiQuery("/payment-plans/all");
  const plans = plansData || [];
  const [isCreating, setIsCreating] = useState(false);

  const { data: scheduleDoc, refetch: refetchSchedule } = useApiQuery("/documents/paymentSchedule");
  const [isUploadingSchedule, setIsUploadingSchedule] = useState(false);

  const [newPlan, setNewPlan] = useState(emptyPlan);
  const [scheduleFile, setScheduleFile] = useState(null);
  const [toast, setToast] = useState("");

  const handleCreate = async () => {
    if (!newPlan.name.trim()) return;
    setIsCreating(true);
    try {
      await apiClient.post("/payment-plans", {
        name: newPlan.name,
        points: newPlan.points
          .split("\n")
          .map((p) => p.trim())
          .filter(Boolean),
        badge: newPlan.badge,
        order: Number(newPlan.order) || 0,
        isActive: newPlan.isActive,
      });
      setNewPlan(emptyPlan);
      setToast("Plan created.");
      refetch();
    } catch (err) {
      setToast(err?.response?.data?.message || "Failed to create plan.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      await apiClient.put(`/payment-plans/${plan._id}`, { isActive: !plan.isActive });
      refetch();
    } catch (err) {
      setToast(err?.response?.data?.message || "Failed to update plan.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/payment-plans/${id}`);
      setToast("Plan deleted.");
      refetch();
    } catch (err) {
      setToast(err?.response?.data?.message || "Failed to delete plan.");
    }
  };

  const uploadSchedule = async () => {
    if (!scheduleFile) return;
    const formData = new FormData();
    formData.append("file", scheduleFile);
    setIsUploadingSchedule(true);
    try {
      await apiClient.post("/documents/paymentSchedule", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setScheduleFile(null);
      setToast("Payment schedule PDF uploaded.");
      refetchSchedule();
    } catch (err) {
      setToast(err?.response?.data?.message || "Failed to upload PDF.");
    } finally {
      setIsUploadingSchedule(false);
    }
  };

  if (isLoading) return <Typography>Loading…</Typography>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Payment Plans
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Payment Schedule PDF
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {scheduleDoc?.fileUrl ? (
            <a href={resolveFileUrl(scheduleDoc.fileUrl)} target="_blank" rel="noreferrer">
              View current schedule PDF
            </a>
          ) : (
            "No schedule PDF uploaded yet."
          )}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <Button component="label" startIcon={<UploadRoundedIcon />} variant="outlined">
            {scheduleFile ? scheduleFile.name : "Choose PDF"}
            <input
              type="file"
              accept="application/pdf"
              hidden
              onChange={(e) => setScheduleFile(e.target.files[0])}
            />
          </Button>
          <Button
            variant="contained"
            onClick={uploadSchedule}
            disabled={!scheduleFile || isUploadingSchedule}
          >
            {isUploadingSchedule ? "Uploading…" : "Upload"}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Add New Plan
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Name"
              value={newPlan.name}
              onChange={(e) => setNewPlan((p) => ({ ...p, name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Badge"
              value={newPlan.badge}
              onChange={(e) => setNewPlan((p) => ({ ...p, badge: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="number"
              label="Order"
              value={newPlan.order}
              onChange={(e) => setNewPlan((p) => ({ ...p, order: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Points (one per line)"
              value={newPlan.points}
              onChange={(e) => setNewPlan((p) => ({ ...p, points: e.target.value }))}
            />
          </Grid>
        </Grid>
        <Button
          sx={{ mt: 2 }}
          startIcon={<AddRoundedIcon />}
          variant="contained"
          onClick={handleCreate}
          disabled={isCreating || !newPlan.name.trim()}
        >
          Add Plan
        </Button>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Existing Plans
        </Typography>
        {plans.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No plans yet.
          </Typography>
        )}
        {plans.map((p, idx) => (
          <Box key={p._id}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 1.5,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Box>
                <Typography fontWeight={700}>
                  {p.name} {p.badge ? `• ${p.badge}` : ""}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(p.points || []).join(" · ")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch checked={p.isActive} onChange={() => handleToggleActive(p)} />
                  }
                  label="Active"
                />
                <IconButton color="error" onClick={() => handleDelete(p._id)}>
                  <DeleteRoundedIcon />
                </IconButton>
              </Box>
            </Box>
            {idx < plans.length - 1 && <Divider />}
          </Box>
        ))}
      </Paper>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast("")}>
        <Alert severity="success" onClose={() => setToast("")}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPaymentPlans;
